import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { writeAdminAuditEvent } from 'lib/adminAudit';
import { getExternalDb, getExternalStorage } from 'lib/firebase';
import { markStaticSeoRefreshRequired } from 'lib/seoRefreshStatus';

const RESOURCE_COLLECTION = 'resource_pages';
const RESOURCE_DOWNLOAD_COLLECTION = 'resource_download_offers';
const LEGACY_SERVICE_DOWNLOAD_COLLECTION = 'service_download_offers';
const DEFAULT_HIDDEN_STATUS = 'default_hidden';
const ARCHIVED_STATUS = 'archived';
const MAX_DOWNLOAD_PACKAGE_BYTES = 50 * 1024 * 1024;
const ALLOWED_DOWNLOAD_PACKAGE_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/octet-stream',
  '',
]);

function getDbOrThrow() {
  const db = getExternalDb();
  if (!db) {
    throw new Error('Resource CMS is not configured. Check Firebase environment variables.');
  }
  return db;
}

function getStorageOrThrow() {
  const storage = getExternalStorage();
  if (!storage) {
    throw new Error('Resource download storage is not configured. Check Firebase environment variables.');
  }
  return storage;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sanitizeResourceText(value) {
  return String(value || '').trim();
}

export function slugifyResourcePage(value) {
  return sanitizeResourceText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
}

function splitTextList(value, maxItems = 60) {
  if (Array.isArray(value)) {
    return value.map(sanitizeResourceText).filter(Boolean).slice(0, maxItems);
  }

  return sanitizeResourceText(value)
    .split(/\n+/)
    .map(sanitizeResourceText)
    .filter(Boolean)
    .slice(0, maxItems);
}

function normalizeFaqs(value) {
  if (typeof value === 'string') {
    return value
      .split(/\n+/)
      .map(sanitizeResourceText)
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('|').map(sanitizeResourceText);
        return [parts[0] || '', parts.slice(1).join(' | ') || ''];
      })
      .filter(([question, answer]) => question && answer)
      .slice(0, 30);
  }

  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (Array.isArray(entry)) {
        return [sanitizeResourceText(entry[0]), sanitizeResourceText(entry[1])];
      }
      return [sanitizeResourceText(entry?.question), sanitizeResourceText(entry?.answer)];
    })
    .filter(([question, answer]) => question && answer)
    .slice(0, 30);
}

function sanitizeDownloadFileName(value) {
  return sanitizeResourceText(value)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 140)
    || 'resource-download.zip';
}

function normalizeFileVersion(value) {
  return sanitizeResourceText(value)
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    || 'v1.0';
}

function normalizeStatus(value) {
  const status = sanitizeResourceText(value);
  if (status === 'published') return 'published';
  if (status === ARCHIVED_STATUS) return ARCHIVED_STATUS;
  if (status === DEFAULT_HIDDEN_STATUS) return DEFAULT_HIDDEN_STATUS;
  return 'draft';
}

function normalizeDownloadOffer(value = {}) {
  const raw = value && typeof value === 'object' ? value : {};
  const enabled = raw.enabled === true || sanitizeResourceText(raw.enabled) === 'true';
  const offerId = slugifyResourcePage(raw.offerId || raw.title || raw.fileName);

  return {
    enabled,
    offerId,
    title: sanitizeResourceText(raw.title),
    description: sanitizeResourceText(raw.description),
    buttonLabel: sanitizeResourceText(raw.buttonLabel) || 'Get Download Link',
    fileName: sanitizeResourceText(raw.fileName),
    fileVersion: sanitizeResourceText(raw.fileVersion),
    storagePath: sanitizeResourceText(raw.storagePath),
    downloadUrl: sanitizeResourceText(raw.downloadUrl),
    deliveryMode: 'verified_download_access',
    successMessage: sanitizeResourceText(raw.successMessage) || 'Check your email to confirm access. The download link is sent after confirmation.',
    requireEmailConfirmation: true,
  };
}

function normalizeResourcePage(data = {}, id = '') {
  const title = sanitizeResourceText(data.title);
  const slug = slugifyResourcePage(data.slug || title);
  const status = normalizeStatus(data.status);
  const downloadOffer = normalizeDownloadOffer(data.downloadOffer);

  return {
    id: id || sanitizeResourceText(data.id),
    slug,
    status,
    public: status === 'published',
    category: sanitizeResourceText(data.category) || 'Resources',
    sortOrder: Number.isFinite(Number(data.sortOrder)) ? Number(data.sortOrder) : 100,
    title,
    description: sanitizeResourceText(data.description),
    intro: sanitizeResourceText(data.intro),
    sections: splitTextList(data.sections, 80),
    faqs: normalizeFaqs(data.faqs),
    relatedPath: sanitizeResourceText(data.relatedPath) || '/services',
    relatedLabel: sanitizeResourceText(data.relatedLabel) || 'View Services',
    seoTitle: sanitizeResourceText(data.seoTitle) || title,
    metaDescription: sanitizeResourceText(data.metaDescription) || sanitizeResourceText(data.description),
    canonicalPath: `/resources/${slug}`,
    downloadOffer,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdBy: sanitizeResourceText(data.createdBy),
    updatedBy: sanitizeResourceText(data.updatedBy),
  };
}

function normalizeSnapshot(snapshot) {
  return normalizeResourcePage(snapshot.data() || {}, snapshot.id);
}

function publicDownloadOffer(value = {}) {
  const offer = normalizeDownloadOffer(value);
  return {
    enabled: offer.enabled,
    offerId: offer.offerId,
    title: offer.title,
    description: offer.description,
    buttonLabel: offer.buttonLabel,
    fileName: offer.fileName,
    fileVersion: offer.fileVersion,
    successMessage: offer.successMessage,
    requireEmailConfirmation: true,
  };
}

function publicResourcePage(page) {
  return {
    ...page,
    downloadOffer: publicDownloadOffer(page.downloadOffer),
  };
}

function sortResourcePages(pages) {
  return [...pages].sort((a, b) => {
    const orderDiff = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
    if (orderDiff !== 0) return orderDiff;
    return String(a.title).localeCompare(String(b.title));
  });
}

export function resourceListToText(value) {
  return splitTextList(value, 100).join('\n');
}

export function resourceFaqsToText(value) {
  return normalizeFaqs(value).map(([question, answer]) => `${question} | ${answer}`).join('\n');
}

export async function listPublishedResourcePages() {
  try {
    const db = getDbOrThrow();
    const snapshot = await getDocs(
      query(collection(db, RESOURCE_COLLECTION), where('status', '==', 'published'))
    );
    return sortResourcePages(snapshot.docs.map(normalizeSnapshot).filter((page) => page.slug && page.title)).map(publicResourcePage);
  } catch (error) {
    const reason = String(error?.message || '').includes('Resource CMS is not configured')
      ? 'Resource CMS is not configured'
      : 'Failed to load resource pages from Firestore';
    console.error(reason, error);
    return [];
  }
}

export async function getPublishedResourcePageBySlug(slug) {
  const normalizedSlug = slugifyResourcePage(slug);
  if (!normalizedSlug) return null;
  const pages = await listPublishedResourcePages();
  return pages.find((page) => page.slug === normalizedSlug) || null;
}

export async function listAdminResourcePages() {
  const db = getDbOrThrow();
  const snapshot = await getDocs(collection(db, RESOURCE_COLLECTION));
  return sortResourcePages(snapshot.docs.map(normalizeSnapshot).filter((page) => page.status !== DEFAULT_HIDDEN_STATUS));
}

export async function getAdminResourcePage(pageId) {
  const id = sanitizeResourceText(pageId);
  const db = getDbOrThrow();
  const snapshot = await getDoc(doc(db, RESOURCE_COLLECTION, id));
  if (!snapshot.exists()) return null;
  const page = normalizeSnapshot(snapshot);
  if (!page.downloadOffer.offerId) return page;

  try {
    let offerSnapshot = await getDoc(doc(db, RESOURCE_DOWNLOAD_COLLECTION, page.downloadOffer.offerId));
    if (!offerSnapshot.exists()) {
      offerSnapshot = await getDoc(doc(db, LEGACY_SERVICE_DOWNLOAD_COLLECTION, page.downloadOffer.offerId));
    }
    if (!offerSnapshot.exists()) return page;
    return {
      ...page,
      downloadOffer: normalizeDownloadOffer({
        ...page.downloadOffer,
        ...(offerSnapshot.data() || {}),
      }),
    };
  } catch (error) {
    console.warn('Failed to load private resource download offer metadata.', error);
    return page;
  }
}

function validateResourcePageInput(page) {
  const normalized = normalizeResourcePage(page, page.id);

  if (!normalized.title) throw new Error('Resource title is required.');
  if (!normalized.slug) throw new Error('Resource slug is required.');
  if (!normalized.description) throw new Error('Resource description is required.');

  if (normalized.status === 'published') {
    if (!normalized.intro) throw new Error('Published resources require intro copy.');
    if (!normalized.metaDescription) throw new Error('Published resources require a meta description.');
    if (normalized.sections.length < 2) throw new Error('Published resources require at least two review points.');
  }

  if (normalized.downloadOffer.enabled) {
    if (!normalized.downloadOffer.offerId) throw new Error('Enabled downloads require an offer ID.');
    if (!normalized.downloadOffer.title) throw new Error('Enabled downloads require a title.');
    if (!normalized.downloadOffer.description) throw new Error('Enabled downloads require description copy.');
    if (!normalized.downloadOffer.buttonLabel) throw new Error('Enabled downloads require a button label.');
    if (!normalized.downloadOffer.storagePath && !normalized.downloadOffer.downloadUrl) {
      throw new Error('Enabled downloads require a private Storage path or saved download URL.');
    }
  }

  return normalized;
}

async function assertUniqueSlug(slug, currentPageId = '') {
  const pages = await listAdminResourcePages();
  const conflict = pages.find((page) => page.slug === slug && page.id !== currentPageId);
  if (conflict) {
    throw new Error(`The slug "${slug}" is already used by another resource page.`);
  }
}

function buildWritePayload(normalized, adminEmail) {
  return {
    slug: normalized.slug,
    status: normalized.status === ARCHIVED_STATUS ? 'draft' : normalized.status,
    public: normalized.status === 'published',
    category: normalized.category,
    sortOrder: normalized.sortOrder,
    title: normalized.title,
    description: normalized.description,
    intro: normalized.intro,
    sections: normalized.sections,
    faqs: normalized.faqs.map(([question, answer]) => ({ question, answer })),
    relatedPath: normalized.relatedPath,
    relatedLabel: normalized.relatedLabel,
    seoTitle: normalized.seoTitle,
    metaDescription: normalized.metaDescription,
    canonicalPath: `/resources/${normalized.slug}`,
    downloadOffer: publicDownloadOffer(normalized.downloadOffer),
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeResourceText(adminEmail),
  };
}

export function buildResourceDownloadStoragePath({ offerId, fileVersion, fileName }) {
  const safeOfferId = slugifyResourcePage(offerId);
  if (!safeOfferId) throw new Error('Upload requires a download offer ID.');
  const safeVersion = normalizeFileVersion(fileVersion);
  const safeFileName = sanitizeDownloadFileName(fileName);
  if (!safeFileName.toLowerCase().endsWith('.zip')) {
    throw new Error('Resource download packages must be ZIP files.');
  }
  return `resource-downloads/${safeOfferId}/${safeVersion}/${safeFileName}`;
}

export async function uploadResourceDownloadPackage({ file, offerId, fileVersion = 'v1.0', adminEmail = '', onProgress } = {}) {
  if (!file) throw new Error('Choose a ZIP package to upload.');
  if (!ALLOWED_DOWNLOAD_PACKAGE_TYPES.has(file.type || '')) {
    throw new Error('Only ZIP packages can be uploaded.');
  }
  if (!String(file.name || '').toLowerCase().endsWith('.zip')) {
    throw new Error('Only .zip packages can be uploaded.');
  }
  if (file.size > MAX_DOWNLOAD_PACKAGE_BYTES) {
    throw new Error('Download packages must be smaller than 50 MB.');
  }

  const storage = getStorageOrThrow();
  const safeVersion = normalizeFileVersion(fileVersion);
  const safeFileName = sanitizeDownloadFileName(file.name);
  const storagePath = buildResourceDownloadStoragePath({ offerId, fileVersion: safeVersion, fileName: safeFileName });
  const storageRef = ref(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'application/zip',
      customMetadata: {
        offerId: slugifyResourcePage(offerId),
        fileVersion: safeVersion,
        uploadedAt: new Date().toISOString(),
      },
    });

    task.on(
      'state_changed',
      (snapshot) => {
        if (typeof onProgress === 'function') {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      reject,
      resolve
    );
  });
  const downloadUrl = await getDownloadURL(storageRef);

  await writeAdminAuditEvent({
    action: 'uploaded-resource-package',
    targetCollection: RESOURCE_DOWNLOAD_COLLECTION,
    targetId: slugifyResourcePage(offerId),
    summary: 'Resource download package uploaded',
    adminEmail,
    metadata: {
      fileName: safeFileName,
      fileVersion: safeVersion,
      size: file.size,
      contentType: file.type || 'application/zip',
    },
  });

  return {
    storagePath,
    downloadUrl,
    fileName: safeFileName,
    fileVersion: safeVersion,
    size: file.size,
    contentType: file.type || 'application/zip',
  };
}

export async function deleteResourceDownloadPackage(storagePath, adminEmail = '') {
  const normalizedPath = sanitizeResourceText(storagePath);
  if (!normalizedPath.startsWith('resource-downloads/')) {
    throw new Error('Only resource download packages can be deleted from this editor.');
  }
  const storage = getStorageOrThrow();
  await deleteObject(ref(storage, normalizedPath));
  await writeAdminAuditEvent({
    action: 'deleted-resource-package',
    targetCollection: RESOURCE_DOWNLOAD_COLLECTION,
    targetId: normalizedPath.split('/')[1] || '',
    summary: 'Resource download package deleted',
    adminEmail,
    metadata: {
      fileName: normalizedPath.split('/').pop() || '',
      version: normalizedPath.split('/')[2] || '',
    },
  });
}

async function saveResourceDownloadOffer(db, normalized, adminEmail) {
  const offer = normalized.downloadOffer;
  if (!offer.offerId) return;

  const payload = {
    enabled: offer.enabled,
    offerId: offer.offerId,
    sourceType: 'resource',
    resourceSlug: normalized.slug,
    resourceTitle: normalized.title,
    title: offer.title,
    description: offer.description,
    buttonLabel: offer.buttonLabel,
    fileName: offer.fileName,
    fileVersion: offer.fileVersion,
    storagePath: offer.storagePath,
    downloadUrl: offer.downloadUrl,
    deliveryMode: 'verified_download_access',
    successMessage: offer.successMessage,
    requiresEmailConfirmation: true,
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeResourceText(adminEmail),
  };

  await Promise.all([
    setDoc(doc(db, RESOURCE_DOWNLOAD_COLLECTION, offer.offerId), payload, { merge: true }),
    setDoc(doc(db, LEGACY_SERVICE_DOWNLOAD_COLLECTION, offer.offerId), payload, { merge: true }),
  ]);
}

export async function saveResourcePage({ pageId = '', page, adminEmail = '' }) {
  const db = getDbOrThrow();
  const normalized = validateResourcePageInput(page);
  await assertUniqueSlug(normalized.slug, pageId);

  const payload = buildWritePayload(normalized, adminEmail);

  if (pageId) {
    await updateDoc(doc(db, RESOURCE_COLLECTION, pageId), payload);
    await saveResourceDownloadOffer(db, normalized, adminEmail);
    await writeAdminAuditEvent({
      action: payload.public ? 'updated-published-resource' : 'updated-draft-resource',
      targetCollection: RESOURCE_COLLECTION,
      targetId: pageId,
      summary: 'Resource page saved',
      adminEmail,
      metadata: {
        slug: payload.slug,
        status: payload.status,
        title: payload.title,
        downloadOfferEnabled: normalized.downloadOffer.enabled,
      },
    });
    if (payload.public) {
      await markStaticSeoRefreshRequired({
        action: 'updated',
        source: 'resource',
        path: `/resources/${payload.slug}`,
        title: payload.title,
        adminEmail,
      }).catch((error) => {
        console.error('Failed to mark static SEO refresh required after resource update:', error);
      });
    }
    return pageId;
  }

  const created = await addDoc(collection(db, RESOURCE_COLLECTION), {
    ...payload,
    createdAt: serverTimestamp(),
    createdBy: sanitizeResourceText(adminEmail),
  });
  await saveResourceDownloadOffer(db, normalized, adminEmail);
  await writeAdminAuditEvent({
    action: payload.public ? 'created-published-resource' : 'created-draft-resource',
    targetCollection: RESOURCE_COLLECTION,
    targetId: created.id,
    summary: 'Resource page created',
    adminEmail,
    metadata: {
      slug: payload.slug,
      status: payload.status,
      title: payload.title,
      downloadOfferEnabled: normalized.downloadOffer.enabled,
    },
  });
  if (payload.public) {
    await markStaticSeoRefreshRequired({
      action: 'published',
      source: 'resource',
      path: `/resources/${payload.slug}`,
      title: payload.title,
      adminEmail,
    }).catch((error) => {
      console.error('Failed to mark static SEO refresh required after resource publish:', error);
    });
  }
  return created.id;
}

export async function archiveResourcePage(page, adminEmail = '') {
  const db = getDbOrThrow();
  const id = sanitizeResourceText(page?.id);
  const slug = slugifyResourcePage(page?.slug);
  if (!id || !slug) {
    throw new Error('Resource page ID and slug are required.');
  }

  await updateDoc(doc(db, RESOURCE_COLLECTION, id), {
    status: ARCHIVED_STATUS,
    public: false,
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeResourceText(adminEmail),
  });
  await writeAdminAuditEvent({
    action: 'archived-resource',
    targetCollection: RESOURCE_COLLECTION,
    targetId: id,
    summary: 'Resource page archived',
    adminEmail,
    metadata: { slug, title: page.title },
  });

  await markStaticSeoRefreshRequired({
    action: 'archived',
    source: 'resource',
    path: `/resources/${slug}`,
    title: page.title || 'Resource page archived',
    adminEmail,
  }).catch((error) => {
    console.error('Failed to mark static SEO refresh required after resource archive:', error);
  });
}

export function formatResourceDate(value) {
  const millis = timestampToMillis(value);
  if (!millis) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(millis));
}
